import React, { useEffect, useState } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, Select, Space, Tag,
  Typography, Alert, Popconfirm, message, Empty,
} from 'antd';
import { PlusOutlined, WarningOutlined, DeleteOutlined, EditOutlined, KeyOutlined } from '@ant-design/icons';
import { fiscalAdminService, adminService } from '../../services/api';

const { Title } = Typography;
const { Text } = Typography;

/**
 * Alta y administración de perfiles fiscal-admin.
 *
 * Otorgar, modificar o retirar un perfil de staff es exclusivo del Admin.
 */
const FiscalAdmins = () => {
  const [perfiles, setPerfiles] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [sinCobertura, setSinCobertura] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const [modalAlta, setModalAlta] = useState(false);
  const [carteraEnEdicion, setCarteraEnEdicion] = useState(null);
  const [passwordEnEdicion, setPasswordEnEdicion] = useState(null);
  const [form] = Form.useForm();
  const [formCartera] = Form.useForm();
  const [formPassword] = Form.useForm();

  useEffect(() => {
    fetchTodo();
  }, []);

  const fetchTodo = async () => {
    try {
      const [perfilesData, planesData, coberturaData] = await Promise.all([
        fiscalAdminService.listFiscalAdmins(),
        adminService.getPlans(),
        fiscalAdminService.getPlanesSinCobertura(),
      ]);
      setPerfiles(perfilesData || []);
      setPlanes(planesData || []);
      setSinCobertura(coberturaData);
    } catch (err) {
      setError('Error al cargar los perfiles fiscal-admin');
      console.error('Error fetching fiscal-admins:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCrear = async (values) => {
    setGuardando(true);
    try {
      await fiscalAdminService.createFiscalAdmin(values);
      message.success('Perfil fiscal-admin creado');
      setModalAlta(false);
      form.resetFields();
      await fetchTodo();
    } catch (err) {
      message.error(err?.response?.data?.error || 'No se pudo crear el perfil');
    } finally {
      setGuardando(false);
    }
  };

  const handleGuardarCartera = async (values) => {
    setGuardando(true);
    try {
      await fiscalAdminService.updateCartera(carteraEnEdicion.id, values.plan_ids || []);
      message.success('Cartera actualizada');
      setCarteraEnEdicion(null);
      await fetchTodo();
    } catch (err) {
      message.error(err?.response?.data?.error || 'No se pudo actualizar la cartera');
    } finally {
      setGuardando(false);
    }
  };

  const handleResetPassword = async (values) => {
    setGuardando(true);
    try {
      await fiscalAdminService.resetPassword(passwordEnEdicion.id, values.password);
      message.success('Contraseña actualizada');
      setPasswordEnEdicion(null);
      formPassword.resetFields();
    } catch (err) {
      message.error(err?.response?.data?.error || 'No se pudo actualizar la contraseña');
    } finally {
      setGuardando(false);
    }
  };

  const handleRetirar = async (id) => {
    try {
      await fiscalAdminService.removeFiscalAdmin(id);
      message.success('Perfil de staff retirado');
      await fetchTodo();
    } catch (err) {
      message.error(err?.response?.data?.error || 'No se pudo retirar el perfil');
    }
  };

  const opcionesPlanes = (planes || []).map((p) => ({ label: p.name, value: p.id }));

  const columns = [
    { title: 'Nombre', dataIndex: 'name', key: 'name', render: (v) => <Text strong>{v}</Text> },
    { title: 'Correo', dataIndex: 'email', key: 'email' },
    {
      title: 'Cartera',
      dataIndex: 'cartera_planes',
      key: 'cartera',
      render: (planes) =>
        planes?.length
          ? planes.map((p) => <Tag color="cyan" key={p.id}>{p.name}</Tag>)
          : <Tag>Sin planes</Tag>,
    },
    { title: 'Clientes', dataIndex: 'clientes_en_cartera', key: 'clientes' },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, perfil) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setCarteraEnEdicion(perfil);
              formCartera.setFieldsValue({ plan_ids: perfil.cartera_plan_ids });
            }}
          >
            Cartera
          </Button>
          <Button
            size="small"
            icon={<KeyOutlined />}
            onClick={() => {
              setPasswordEnEdicion(perfil);
              formPassword.resetFields();
            }}
          >
            Contraseña
          </Button>
          <Popconfirm
            title="¿Retirar el perfil de staff?"
            description="La cuenta se conserva, pero pierde el acceso al panel fiscal."
            onConfirm={() => handleRetirar(perfil.id)}
            okText="Retirar"
            cancelText="Cancelar"
          >
            <Button size="small" danger icon={<DeleteOutlined />}>Retirar</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (error) {
    return <Alert type="error" message={error} showIcon />;
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {/* Un subplan sin asignar deja clientes sin gestión y sin error visible */}
      {sinCobertura?.total > 0 && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message="Hay planes activos sin cobertura"
          description={
            <>
              <div style={{ marginBottom: 8 }}>
                Ningún fiscal-admin gestiona estos planes, así que sus clientes no aparecen en
                ninguna cartera:
              </div>
              {sinCobertura.planes.map((p) => (
                <Tag color="orange" key={p.id}>{p.name}</Tag>
              ))}
            </>
          }
        />
      )}

      <Card
        title={<Title level={4} style={{ margin: 0 }}>Perfiles fiscal-admin</Title>}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalAlta(true)}>
            Nuevo fiscal-admin
          </Button>
        }
      >
        <Table
          rowKey="id"
          loading={loading}
          dataSource={perfiles}
          columns={columns}
          pagination={false}
          scroll={{ x: true }}
          locale={{ emptyText: <Empty description="Sin perfiles fiscal-admin" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        />
      </Card>

      <Modal
        title="Nuevo perfil fiscal-admin"
        open={modalAlta}
        onCancel={() => setModalAlta(false)}
        onOk={() => form.submit()}
        confirmLoading={guardando}
        okText="Crear"
        cancelText="Cancelar"
      >
        <Form form={form} layout="vertical" onFinish={handleCrear}>
          <Form.Item name="name" label="Nombre" rules={[{ required: true, message: 'Requerido' }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label="Correo"
            rules={[{ required: true, message: 'Requerido' }, { type: 'email', message: 'Correo inválido' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="password" label="Contraseña" rules={[{ required: true, message: 'Requerido' }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="plan_ids"
            label="Cartera de planes"
            extra="Los clientes de estos planes formarán su cartera. Se puede editar después."
          >
            <Select mode="multiple" options={opcionesPlanes} placeholder="Selecciona planes" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Cartera de ${carteraEnEdicion?.name || ''}`}
        open={!!carteraEnEdicion}
        onCancel={() => setCarteraEnEdicion(null)}
        onOk={() => formCartera.submit()}
        confirmLoading={guardando}
        okText="Guardar"
        cancelText="Cancelar"
      >
        <Form form={formCartera} layout="vertical" onFinish={handleGuardarCartera}>
          <Form.Item
            name="plan_ids"
            label="Planes asignados"
            extra="Quitar un plan saca de su cartera a los clientes de ese plan."
          >
            <Select mode="multiple" options={opcionesPlanes} placeholder="Selecciona planes" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Contraseña de ${passwordEnEdicion?.name || ''}`}
        open={!!passwordEnEdicion}
        onCancel={() => setPasswordEnEdicion(null)}
        onOk={() => formPassword.submit()}
        confirmLoading={guardando}
        okText="Actualizar"
        cancelText="Cancelar"
      >
        <Form form={formPassword} layout="vertical" onFinish={handleResetPassword}>
          <Form.Item
            name="password"
            label="Contraseña nueva"
            rules={[
              { required: true, message: 'Requerido' },
              { min: 6, message: 'Mínimo 6 caracteres' },
            ]}
            extra="La contraseña anterior deja de ser válida de inmediato."
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default FiscalAdmins;
